/**
 * @documental
 * @export useUrlTabState
 * @type
 * hook
 * @import
 * import { useUrlTabState } from "capitalrx-components-2.0/lib/general";
 */

import { useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router";
import { SelectedTabChangeHandler } from "./SelectedTabChangeHandler";

/**
 * Tracks tab state using a specified URL parameter.
 * @param name The name of the URL parameter to track the tab state through.
 * @param initialSelectedTabId The optional initially selected tab ID, if not specified the first tab is selected.
 * @returns A tuple containing the currently selected tab ID and tab change handler function.
 */
export function useUrlTabState(name: string, initialSelectedTabId?: string | null): [string | null | undefined, SelectedTabChangeHandler] {
  const navigate = useNavigate();
  const location = useLocation();
  const { pathname, search, state } = location;

  const useInitialSelectedTabIdRef = useRef<boolean>(true);
  const parsedSelectedTab = new URLSearchParams(search).get(name);
  let selectedTab: string | null | undefined;

  if (parsedSelectedTab === null) {
    if (useInitialSelectedTabIdRef.current) {
      selectedTab = initialSelectedTabId;
    } else {
      selectedTab = undefined;
    }
  } else {
    selectedTab = parsedSelectedTab;
  }

  const tabChangeHandler = useCallback<SelectedTabChangeHandler>(
    (selectedTabId) => {
      const searchParams = new URLSearchParams(search);

      if (selectedTabId === null) {
        searchParams.delete(name);
        useInitialSelectedTabIdRef.current = true;
      } else {
        searchParams.set(name, selectedTabId);
        useInitialSelectedTabIdRef.current = false;
      }

      const entry = {
        pathname,
        search: searchParams.toString(),
        state,
      };

      if (selectedTab === null || selectedTab === undefined) {
        navigate(entry, { replace: true });
      } else {
        navigate(entry);
      }
    },
    [name, pathname, search, state, selectedTab],
  );

  return [selectedTab, tabChangeHandler];
}
